import { html, nothing, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { MobxLitElement } from '@adobe/lit-mobx';
import { runInAction } from 'mobx';
import comsStore from '@mfe-4cs/state/ComsStore';
import Service from '@mfe-4cs/util/Service';
import { TefComs } from '@mfe-4cs/types/typings';
import Tracker from '@mfe-4cs/classes/Tracker';
import { TemplatingMixin } from '@mfec/lit/mixins/TemplatingMixin';
import { templates } from '@mfe-4cs/components/templates';
import './TefComsTriStateButton';
import './TefComsPagination';
import './tef-coms-customer-attribute';

/**
 * The custom element `<tef-coms4cs>`.
 */
export class TefComs4csMicroFrontend extends TemplatingMixin(MobxLitElement, 'occl', templates) {
  @property({ type: String }) apiUri = '';
  @property({ type: String }) agentToken = '';
  @property({ type: String }) gatewayToken = '';
  @property({ type: String }) lifecycle = '';
  @property({ type: String }) mode = '';
  @property({ type: String }) traceid = '';
  @property({ type: String }) occlUri = '';
  @property({ type: String }) styleContext = '';
  @property({ type: String }) consentwhitelist = '';
  @property({ type: String }) channel = '';
  @property({ type: String }) usertype = '';
  @property({ type: String }) vonr = '';
  @property({ type: String }) references = '';
  @property({ type: Boolean }) showReferences = false;
  @property({ type: Boolean }) showVersions = false;
  @property({ type: Boolean }) isExistingRegister = false;
  @property({ type: Boolean }) isNewCustomer = false;
  @property({ type: String }) spId = '';
  @property({ type: String }) lcId = '';
  @property({ type: String }) registerId = '';
  @property({ type: String }) fetch = '';

  store = comsStore;
  nodeTreeCompletelyRendered = false;
  private service = new Service(this); // binding service to <tef-coms4cs>
  private tracker = new Tracker();

  async connectedCallback(): Promise<void> {
    this.setTemplatingTheme(this.occlUri ? 'occl' : 'tefOne');

    try {
      comsStore.reset();
      // show more infos if debugmode is on
      if (localStorage.getItem('tef-coms-4cs-debug-mode')) {
        let timestampInit = 0;
        let timestampReady = 0;

        this.addEventListener('tef-coms4cs', (e) => {
          const { detail } = e as TefComs.Event;
          const channel = detail.level === 'error' ? 'error' : 'info';
          // eslint-disable-next-line no-console
          console[channel]('tef-coms: event', detail.name, e);
          if (detail.name === 'config/INIT') {
            timestampInit = (performance && performance.now) ? performance.now() : 0;
          }
          if (detail.name === 'load/READY') {
            timestampReady = (performance && performance.now) ? performance.now() : 0;
            // const el = <TefComs4csMicroFrontend>customElements.get('tef-coms4cs');
            const infoVersion = `version ${TefComs4csMicroFrontend.version}`;
            const infoReady = `Micro-frontend ready in ${Math.trunc(timestampReady - timestampInit)} ms, `;
            // eslint-disable-next-line no-console
            console.log(infoReady + infoVersion);
          }
        });
      }
      // issue 'config/INIT' immediately because it is used for performance measurements
      this.service.fireEvent<TefComs.EventDetails>({
        name: 'config/INIT',
        level: 'info',
        message: 'micro-frontend is initializing',

      });
      super.connectedCallback(); // start rendering immediately !!

      await this.store.init({
        apiUri: this.apiUri,
        agentToken: this.agentToken,
        gatewayToken: this.gatewayToken,
        lifecycle: this.lifecycle,
        mode: this.mode,
        traceid: this.traceid,
        occlUri: this.occlUri,
        styleContext: this.styleContext,
        consentwhitelist: this.consentwhitelist,
        channel: this.channel,
        usertype: this.usertype,
        vonr: this.vonr,
        references: this.references,
        showReferences: this.showReferences,
        showVersions: this.showVersions,
        isExistingRegister: this.isExistingRegister,
        isNewCustomer: this.isNewCustomer,
        spId: this.spId,
        lcId: this.lcId,
        registerId: this.registerId,
        fetch: this.fetch,
      });

      this.shadowRoot?.addEventListener(
        'occl.collapsible.open',
        (event) => {
          const { id: name } = <HTMLDivElement>event?.target;
          this.tracker.clickCollapsable(name);
          const group = this.store.findGroupByName(name);
          if (group) {
            runInAction(() => {
              group.collapsibleOpen = true;
            });
          }
        },
      );

      const { history } = comsStore;
      this.shadowRoot?.addEventListener(
        'occl.pager.open',
        (event) => history?.gotoPage((<CustomEvent>event).detail as number) as void,
      );
      // eslint-disable-next-line sonarjs/no-ignored-exceptions
    } catch (error) {
      // ignore -- has error condition in store
    }
  }

  updated() {
    // WPCSCM-1183: tefOne <one-popover> elements used here don't stop the event propagation by themselves.
    //              This causes a click on the pop over to also trigger the consent <one-checkbox> to be clicked resulting
    //              in a wrongly granted consent.
    if (!this.nodeTreeCompletelyRendered && this.occlUri === '') {
      const onePopovers$ = this.renderRoot.querySelectorAll('one-popover');

      // Assume all popovers are rendered once we have at least 1
      if (onePopovers$.length > 0) {
        this.nodeTreeCompletelyRendered = true;

        const handler = (event : Event) => event.stopPropagation();
        onePopovers$.forEach((el$) => {
          el$.addEventListener('click', handler);
        });
      }
    }
  }

  static get version(): string {
    return _MFE_VERSION; // Filled/replaced by esbuild (see scripts/esbuild/*.ts)
  }

  async save(): Promise<void> {
    await this.store.save();
  }

  async undo() {
    this.store.undo();
    return Promise.resolve();
  }

  cancel() {
    this.store.cancel();
  }

  getConsents(): TefComs.ShortConsent[] {
    return this.store.getShortConsents();
  }

  getConsentStatus(): TefComs.ConsentAndCustomerAttributeSaveType[] {
    return this.store.getConsentStatus();
  }

  getReferenceLength(): number {
    return this.store.references?.length || 0;
  }

  render(): TemplateResult {
    // If fatal error condition (not recoverable), render nothing.
    // Render stylesheet link as early as possible (for performance reasons).
    // Prevent FOUC effect after loading the OCCL resources.
    if (this.store.fatal) {
      return html`<!-- fatal error condition -->`;
    }

    const isLoading = (!this.store.isDataLoaded && this.store.client?.processing)
      || (this.store.occl && !this.store.occl.ready);

    /* eslint-disable @typescript-eslint/indent */
    return html`<one-stack name="tef-coms4cs">
      ${this.store.occl?.renderStylesheet()}
      ${isLoading
        ? nothing
        : this.getTemplateFn('mainTemplate')()
      }
      ${this.mode.includes('buttons')
       ? this.getTemplateFn('buttonsTemplate')() : nothing}
      </one-stack>`;
  }
}
